import React, { useEffect, useState } from 'react';
import { useAuth } from '../lib/stores/auth';
import { useCrypto } from '../lib/stores/crypto';
import './Home.css';

interface Habit {
  id: string;
  name: string;
  emoji: string;
  completed: boolean;
  streak: number;
}

interface Task {
  id: string;
  text: string;
  completed: boolean;
  dueDate?: string;
  timeSpent?: number;
  projectId: string;
}

interface Project {
  id: string;
  name: string;
  color: string;
  tasks: Task[];
  totalTasks: number;
  completedTasks: number;
}

export default function Home() {
  const { user } = useAuth();
  const { isUnlocked } = useCrypto();

  const [currentDate, setCurrentDate] = useState('');
  const [greeting, setGreeting] = useState('');

  // Mock data - will be replaced with API calls
  const [habits] = useState<Habit[]>([
    { id: '1', name: 'Morning run', emoji: '🏃', completed: true, streak: 21 },
    { id: '2', name: 'Read 20 mins', emoji: '📚', completed: true, streak: 14 },
    { id: '3', name: 'Vitamins', emoji: '💊', completed: false, streak: 8 },
    { id: '4', name: 'Meditate', emoji: '🧘', completed: false, streak: 5 },
    { id: '5', name: '8 glasses water', emoji: '💧', completed: false, streak: 3 },
  ]);

  const [projects] = useState<Project[]>([
    {
      id: '1',
      name: 'Q2 Presentation',
      color: 'var(--color-secondary-600)',
      totalTasks: 7,
      completedTasks: 4,
      tasks: [
        { id: '1', text: 'Draft executive summary slide', completed: true, timeSpent: 25, projectId: '1' },
        { id: '2', text: 'Finalise risk heat map data', completed: false, dueDate: 'Due today', projectId: '1' },
        { id: '3', text: 'Review compliance metrics with team', completed: false, dueDate: 'Due today', projectId: '1' },
      ],
    },
    {
      id: '2',
      name: 'CONL701 Deepfakes Research',
      color: 'var(--color-primary-600)',
      totalTasks: 9,
      completedTasks: 6,
      tasks: [
        { id: '4', text: 'Cite Smith & Jones (2025) methodology section', completed: false, dueDate: 'Due tomorrow', projectId: '2' },
      ],
    },
    {
      id: '3',
      name: 'Home Server Maintenance',
      color: 'var(--color-success-600)',
      totalTasks: 3,
      completedTasks: 2,
      tasks: [
        { id: '5', text: 'Update Ollama models', completed: true, timeSpent: 12, projectId: '3' },
        { id: '6', text: 'Check Nextcloud storage usage', completed: false, dueDate: 'Due today', projectId: '3' },
      ],
    },
  ]);

  const [journalStreak] = useState(14);
  const [weeklyStats] = useState({
    tasksCompleted: 14,
    totalTasks: 18,
    habitsOnStreak: 3,
    focusTime: '6.2h',
    goalProgress: { name: 'Run 5K', percentage: 60 },
  });

  const [selectedMood, setSelectedMood] = useState<number | null>(3);
  const [journalEntry, setJournalEntry] = useState('');

  useEffect(() => {
    // Set greeting based on time of day
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good morning');
    else if (hour < 18) setGreeting('Good afternoon');
    else setGreeting('Good evening');

    // Format date
    const options: Intl.DateTimeFormatOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    setCurrentDate(new Date().toLocaleDateString('en-US', options));
  }, []);

  const toggleHabit = (habitId: string) => {
    // TODO: Implement habit toggle API call
    console.log('Toggle habit:', habitId);
  };

  const toggleTask = (taskId: string) => {
    // TODO: Implement task toggle API call
    console.log('Toggle task:', taskId);
  };

  const startFocusTimer = (taskId: string) => {
    // TODO: Implement focus timer
    console.log('Start focus timer for task:', taskId);
  };

  const handleQuickCapture = () => {
    alert('Quick Capture - Coming soon!');
  };

  const moods = ['😫', '😕', '😐', '🙂', '😄'];

  const userName = user?.name?.split(' ')[0] || 'there';

  return (
    <div className="today-content">
      {/* Greeting */}
      <div className="today-greeting">
        <h1>{greeting}, {userName}.</h1>
        <div className="today-date">{currentDate}</div>
      </div>

      {/* Milestone toast */}
      {journalStreak > 0 && (
        <div className="milestone">
          🔥 {journalStreak}-day journal streak
        </div>
      )}

      {/* Habits */}
      <section className="habits-section">
        <div className="section-label">Today's habits</div>
        <div className="habits-row">
          {habits.map((habit) => (
            <div key={habit.id} className="habit-item" onClick={() => toggleHabit(habit.id)}>
              <div className={`habit-circle ${habit.completed ? 'done' : ''}`}>
                {!habit.completed && <span>{habit.emoji}</span>}
              </div>
              <div className="habit-name">{habit.name}</div>
              {habit.streak > 0 && (
                <div className="habit-streak">🔥 {habit.streak}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Tasks */}
      <section className="tasks-section">
        <div className="section-label">Today's tasks</div>
        {projects.map((project) => (
          <div key={project.id} className="task-project-group">
            <div className="task-project-name">
              <span className="dot" style={{ backgroundColor: project.color }}></span>
              {project.name}
              <span className="progress-mini">
                {project.completedTasks}/{project.totalTasks} tasks
              </span>
            </div>
            {project.tasks.map((task) => (
              <div key={task.id} className="task-item">
                <div
                  className={`task-check ${task.completed ? 'done' : ''}`}
                  onClick={() => toggleTask(task.id)}
                />
                <span className={`task-text ${task.completed ? 'done' : ''}`}>
                  {task.text}
                </span>
                {task.completed && task.timeSpent && (
                  <span className="task-timer">✓ {task.timeSpent}m</span>
                )}
                {!task.completed && task.dueDate && (
                  <span className="task-due">{task.dueDate}</span>
                )}
                {!task.completed && !task.timeSpent && (
                  <span
                    className="task-timer"
                    onClick={() => startFocusTimer(task.id)}
                  >
                    ▶ Focus
                  </span>
                )}
              </div>
            ))}
          </div>
        ))}
      </section>

      {/* AI insight (premium preview) */}
      {isUnlocked && (
        <div className="ai-card">
          <div className="ai-label">✦ AI Insight</div>
          <div className="ai-text">
            Your focus sessions are 40% longer on days you run in the morning.
            You've completed your morning run today — a good day for deep work.
          </div>
        </div>
      )}

      {/* Journal */}
      <section className="journal-section">
        <div className="section-label">Journal</div>
        <div className="journal-card">
          <div className="journal-prompt">What gave you energy today?</div>
          <div className="mood-row">
            {moods.map((mood, index) => (
              <div
                key={index}
                className={`mood-face ${selectedMood === index ? 'selected' : ''}`}
                onClick={() => setSelectedMood(index)}
              >
                {mood}
              </div>
            ))}
          </div>
          <textarea
            className="journal-input"
            placeholder="Start writing..."
            value={journalEntry}
            onChange={(e) => setJournalEntry(e.target.value)}
            rows={3}
          />
        </div>
      </section>

      {/* Quick wins */}
      <div className="quick-wins">
        <h4>This week</h4>
        <div className="qw-stats">
          <div className="qw-stat">
            <strong>{weeklyStats.tasksCompleted}</strong> / {weeklyStats.totalTasks} tasks
          </div>
          <div className="qw-stat">
            <strong>{weeklyStats.habitsOnStreak}</strong> habits on streak
          </div>
          <div className="qw-stat">
            <strong>{weeklyStats.focusTime}</strong> focus time
          </div>
          <div className="qw-stat">
            Goal <strong>'{weeklyStats.goalProgress.name}'</strong> at{' '}
            <strong>{weeklyStats.goalProgress.percentage}%</strong>
          </div>
        </div>
      </div>

      {/* FAB */}
      <button className="fab" onClick={handleQuickCapture} title="Quick capture">
        +
      </button>
    </div>
  );
}
